import { assertNever } from "@@/shared/assert-never";
import { asDate } from "@@/shared/date-time/as-date";
import { formatBookedTime } from "@@/shared/date-time/format-booked-time";
import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import {
    isWithinTimeRange,
    type TimeRange,
} from "@@/shared/date-time/time-range";
import { tz } from "@@/shared/date-time/timezone";
import { HttpError } from "@@/shared/errors/http-error";
import type { PayoutTarget } from "@@/shared/financials/payout-targets/payout-target";
import type { OrderEvent } from "@@/shared/orders/order-events/events/_order-event";
import {
    isOrderChangeRefundRequestedEvent,
    orderChangeRefundRequestedEventFactory,
} from "@@/shared/orders/order-events/events/order-change-refund-requested.event";
import {
    isOrderChangedEvent,
    type OrderChangedEvent,
} from "@@/shared/orders/order-events/events/order-changed-event/order-changed.event";
import { isOrderConfirmedEvent } from "@@/shared/orders/order-events/events/order-confirmed.event";
import {
    isOrderCreatedEvent,
    type OrderCreatedEvent,
} from "@@/shared/orders/order-events/events/order-created.event";
import { isOrderFullRefundRequestedEvent } from "@@/shared/orders/order-events/events/order-full-refund-requested.event";
import { isOrderPartialRefundRequestedEvent } from "@@/shared/orders/order-events/events/order-partial-refund-requested.event";
import { isOrderPayRequestFulfilledEvent } from "@@/shared/orders/order-events/events/order-pay-request-events/order-pay-request-fulfilled.event";
import { isOrderPaymentFulfilledEvent } from "@@/shared/orders/order-events/events/order-payment-events/order-payment-fulfilled.event";
import { isOrderPaymentRefundFulfilledEvent } from "@@/shared/orders/order-events/events/order-payment-events/order-payment-refund-fulfilled.event";
import type { OrderId } from "@@/shared/orders/order-id";
import { legacyCustomerServiceFeePrice } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { Provider } from "@@/shared/providers/provider";
import {
    translatedFactory,
    translation,
} from "@@/shared/translations/translation";
import { systemUser } from "@@/shared/users/system-user";
import { isUserId } from "@@/shared/users/user-id";
import { asArray } from "@@/utilities/as-array";
import { maxOf } from "@@/utilities/min-max-of";
import { parseSafely } from "@@/utilities/parse-with-schema";
import { sortBy } from "@@/utilities/sort";
import { sum } from "@@/utilities/sum";
import { toZonedTime } from "date-fns-tz";
import { vatBreakdownFactory } from "../shared/vat-breakdown.factory";
import { parseFulfillmentDate } from "./parse-fulfillment-date";
import { parsePaymentType } from "./parse-payment-type";
import {
    settlementNoteSerialNumberZodSchema,
    type SettlementNote,
} from "./settlement-note";
import { settlementNoteIdFactory } from "./settlement-note-id";
import type { CustomFee } from "./settlements/custom-fee";
import type {
    HardCodedFeeId} from "./settlements/hard-coded-fees";
import {
    hardCodedFeesForSettlements,
    isHardCodedFeeId,
} from "./settlements/hard-coded-fees";
import { legacyServiceFeeIncludedCheck } from "./settlements/legacy-service-fee-included-check";
import { paymentSettlementFactory } from "./settlements/payment.settlement";
import { refundSettlementFactory } from "./settlements/refund.settlement";
import type { Settlement } from "./settlements/settlement";

const isOriginEvent = (
    event: OrderEvent,
): event is OrderCreatedEvent | OrderChangedEvent =>
    isOrderCreatedEvent(event) || isOrderChangedEvent(event);

const settlementNoteFactory = (params: {
    orderEvents: readonly OrderEvent[];
    provider: Provider;
    timeRange: TimeRange;
    payoutTarget: PayoutTarget;
    serialNumber?: string;
    fees: (HardCodedFeeId | CustomFee)[];
    settlementDate?: SettlementNote["settlementDate"];
}): SettlementNote => {
    const { orderEvents, provider, timeRange, payoutTarget } = params;

    const serialNumber = parseSafely({
        schema: settlementNoteSerialNumberZodSchema,
        value: params.serialNumber,
    });
    if (params.serialNumber && !serialNumber) {
        throw new HttpError(
            400,
            translation({
                sv: "Ogiltigt serienummer",
                en: "Invalid serial number",
            }),
        );
    }

    // Group order events by order id
    const orderEventsGroupedByOrderId = orderEvents.reduce((agg, event) => {
        const events = agg.get(event.orderId) ?? [];
        events.push(event);
        agg.set(event.orderId, events);
        return agg;
    }, new Map<OrderId, OrderEvent[]>());

    // Parse all order ids provided
    const orderIds = Array.from(orderEventsGroupedByOrderId.keys());

    // Create settlements
    const settlements: Settlement[] = orderIds
        .flatMap(orderId => {
            // Find all events for the given order id
            const orderEventsForOrder =
                orderEventsGroupedByOrderId.get(orderId) ?? [];

            // If there are none, ignore it end return an empty array
            if (!orderEventsForOrder.length) return [];

            // Find the order created event
            const orderCreatedEvent =
                orderEventsForOrder.find(isOrderCreatedEvent);

            // ...and make sure there is one
            if (!orderCreatedEvent)
                throw new Error(`No order created event for order ${orderId}`);

            // Find the order confirmed event
            // - Doesn't have to be one
            const orderConfirmedEvent = orderEventsForOrder.find(
                isOrderConfirmedEvent,
            );
            // ...but if there is, parse the fulfillment date
            const orderConfirmedFulfillmentDate =
                orderConfirmedEvent?.estimatedDeliveryTime ??
                parseFulfillmentDate(orderCreatedEvent);

            // Find all origin events
            // - All Created and Changed events that is
            const originEvents = orderEventsForOrder.filter(isOriginEvent);
            // const originEventIds = originEvents.map(event => event._id);
            // - And all payment fulfilled events
            const paymentFulfilledEvents = orderEventsForOrder.filter(
                isOrderPaymentFulfilledEvent,
            );
            // - And all pay request fulfilled events
            const payReqFulfilledEvents = orderEventsForOrder.filter(
                isOrderPayRequestFulfilledEvent,
            );
            // - And all refund fulfilled events
            const refundFulfilledEvents = orderEventsForOrder.filter(
                isOrderPaymentRefundFulfilledEvent,
            );

            // Find all settlement events for the order...
            // - All payment, pay request and refund fulfilled events
            const settlementEvents = [
                ...paymentFulfilledEvents,
                ...payReqFulfilledEvents,
                ...refundFulfilledEvents,
            ]
                // ...and filter out events outside of the time range
                .filter(event => {
                    return isWithinTimeRange(event._createdAt, timeRange);
                });

            // For every settlement, create all settlement items needed
            const settlements = settlementEvents.flatMap(
                (settlementEvent): Settlement[] => {
                    // Find all the origin events for the settlement event
                    // - first parse the origin event ids
                    const settlementOriginEventIds =
                        "orderEventId" in settlementEvent.paymentFor
                            ? asArray(settlementEvent.paymentFor.orderEventId)
                            : settlementEvent.paymentFor.orderEventIds;
                    if (settlementEvent.type === "PAYMENT_REFUND_FULFILLED") {
                        //Have to check if the refund is a change refund
                        const event = orderEventsForOrder.find(
                            event =>
                                event._id ===
                                settlementEvent.paymentFor.orderEventId,
                        );
                        if (isOrderChangeRefundRequestedEvent(event)) {
                            settlementOriginEventIds.push(
                                ...event.changeEventIds,
                            );
                        }
                    }

                    // - then filter out the origin events
                    const settlementOriginEvents = originEvents.filter(event =>
                        settlementOriginEventIds.includes(event._id),
                    );

                    // If there are no origin event
                    // then fall back to conclude that the settlement event
                    // has the order created event as origin

                    if (!settlementOriginEvents.length) {
                        settlementOriginEvents.push(orderCreatedEvent);
                    }

                    // Calculate price of previous legacy partial refunds
                    const previousLegacyPartialRefunds =
                        settlementEvents.filter(event => {
                            // Make sure the event only has one origin event
                            // and that it's the order created event
                            const originEventIds =
                                "orderEventIds" in event.paymentFor
                                    ? event.paymentFor.orderEventIds
                                    : asArray(event.paymentFor.orderEventId);
                            if (originEventIds.length !== 1) return false;
                            const originEvent = originEvents.find(
                                event => event._id === originEventIds[0],
                            );
                            if (!originEvent) return false;
                            if (!isOrderCreatedEvent(originEvent)) return false;

                            return (
                                // Make sure the event is a previous event
                                event._createdAt.unix <
                                    settlementEvent._createdAt.unix &&
                                // And make sure the event is a refund fulfilled event
                                isOrderPaymentRefundFulfilledEvent(event)
                            );
                        });
                    const previousLegacyPartialRefundsPrice = sumPrices(
                        previousLegacyPartialRefunds.map(event => {
                            const _event = event;
                            switch (_event.type) {
                                case "PAYMENT_REFUND_FULFILLED":
                                    return _event.refundedAmount;
                                case "PAYMENT_FULFILLED":
                                    return _event.paidAmount;
                                case "PAY_REQUEST_FULFILLED":
                                    return _event.price;
                                default:
                                    assertNever(_event, "Unknown event type", {
                                        valueProp: "type",
                                    });
                            }
                        }),
                    );
                    // console.log(
                    //     "PREVIOUS LEGACY PARTIAL REFUNDS PRICE",
                    //     previousLegacyPartialRefundsPrice,
                    // );

                    switch (settlementEvent.type) {
                        case "PAYMENT_FULFILLED":
                            return paymentSettlementFactory({
                                originEvents: settlementOriginEvents,
                                settlementEvent,
                                orderConfirmedFulfillmentDate,
                                isPayRequest: false,
                                paymentType: parsePaymentType(settlementEvent),
                            });
                        case "PAY_REQUEST_FULFILLED":
                            return paymentSettlementFactory({
                                originEvents: settlementOriginEvents,
                                settlementEvent,
                                orderConfirmedFulfillmentDate,
                                isPayRequest: true,
                                paymentType: "STRIPE", //! WE ONLY SUPPORT STRIPE PAYMENT AT THE MOMENT
                            });
                        case "PAYMENT_REFUND_FULFILLED": {
                            // Find the refund request event
                            let refundRequestedEvent = orderEventsForOrder.find(
                                event =>
                                    event._id ===
                                    settlementEvent.paymentFor.orderEventId,
                            );
                            if (!refundRequestedEvent)
                                throw new Error(
                                    `No refund request event for refund event ${settlementEvent._id}`,
                                );
                            if (
                                !isOrderPartialRefundRequestedEvent(
                                    refundRequestedEvent,
                                ) &&
                                !isOrderFullRefundRequestedEvent(
                                    refundRequestedEvent,
                                ) &&
                                !isOrderChangeRefundRequestedEvent(
                                    refundRequestedEvent,
                                )
                            ) {
                                if (
                                    refundRequestedEvent?.type ===
                                    "ORDER_CHANGED"
                                ) {
                                    //If the refund request event is an order changed event and not a refund request event
                                    //then create a temp one for the note
                                    refundRequestedEvent =
                                        orderChangeRefundRequestedEventFactory({
                                            orderGroupId:
                                                refundRequestedEvent.orderGroupId,
                                            _version:
                                                maxOf(
                                                    orderEventsForOrder.map(
                                                        event => event._version,
                                                    ),
                                                ) + 1,
                                            amount: settlementEvent.refundedAmount,
                                            orderId:
                                                refundRequestedEvent.orderId,
                                            providerId:
                                                refundRequestedEvent.providerId,
                                            providerSlug:
                                                refundRequestedEvent.providerSlug ??
                                                "",
                                            reason: refundRequestedEvent.reason,
                                            requestCreatedBy: isUserId(
                                                settlementEvent.createdBy,
                                            )
                                                ? settlementEvent.createdBy
                                                : systemUser,

                                            changeEventIds: [
                                                refundRequestedEvent._id,
                                            ],
                                        });
                                } else {
                                    throw new Error(
                                        `Refund request event is not a partial or full refund request event refundEventId: ${settlementEvent._id}, refundRequestEventId: ${settlementEvent.paymentFor.orderEventId} of type ${refundRequestedEvent?.type}}`,
                                    );
                                }
                            }

                            return refundSettlementFactory({
                                originEvents: settlementOriginEvents,
                                settlementEvent,
                                orderConfirmedFulfillmentDate,
                                refundRequestedEvent,
                                paymentType: parsePaymentType(settlementEvent),
                                orderCreatedOriginEvent: orderCreatedEvent,
                                previousLegacyPartialRefundsPrice,
                            });
                        }
                        default:
                            assertNever(
                                settlementEvent,
                                "Unknown settlement event type",
                                { valueProp: "type" },
                            );
                    }
                },
            );

            return settlements;
        })
        .sort(sortBy(item => item.settlementEvent._createdAt.iso));

    // console.log("SETTLEMENTS 💜💜💜", settlements);
    // checking isPrice because of bad local data
    const filteredSettlements = settlements.filter(item => {
        return item.paymentType !== "FREE" && item.paymentType !== "ON_PREMISE";
    });
    const settlementPrices = filteredSettlements.map(settlement => {
        // Sum all prices, but if there are old legacy service fees, subtract them first
        return legacyServiceFeeIncludedCheck(settlement)
            ? settlement.type === "PAYMENT"
                ? subtractFromPrice(settlement.price, [
                      legacyCustomerServiceFeePrice,
                  ])
                : sumPrices([settlement.price, legacyCustomerServiceFeePrice])
            : settlement.price;
    });
    // console.log("OUTPUT", settlementAmountsInclVat);
    const totalPrice = sumPrices(settlementPrices);
    const payoutAmount = totalPrice.amountIncludingVat;
    // todo! subtract fees

    const vatBreakdown = vatBreakdownFactory(totalPrice);

    const year = toZonedTime(
        asDate(timeRange.start),
        "Europe/Stockholm",
    ).getFullYear();

    const feesAvailable = hardCodedFeesForSettlements(settlements);
    const fees =
        params.fees?.map(fee => {
            if (isHardCodedFeeId(fee)) {
                return feesAvailable[fee];
            }
            return fee;
        }) ?? [];

    return {
        _id: settlementNoteIdFactory(),
        _type: "SETTLEMENT_NOTE_BETA",
        _createdAt: nowIsoAndUnix(),
        status: "DRAFT",
        _creatorId: "ALFRED",
        title: translatedFactory({
            sv:
                "Avräkningsrapport " +
                formatBookedTime({
                    time: timeRange,
                    currentTimezoneName: tz.EuropeStockholm,
                }),
            en:
                "Settlement note " +
                formatBookedTime({
                    time: timeRange,
                    currentTimezoneName: tz.EuropeStockholm,
                }),
        }),
        ownerId: provider._id,
        timeRange,
        timezone: tz.EuropeStockholm,
        year,
        items: settlements,
        serialNumber,
        settlementDate: params.settlementDate,
        orderAndOrderChangeCount: sum([settlements.length]), //? MAYBE TURN THIS INTO AN OBJECT WITH DIFFERENT TYPES OF COUNT RESULTS?
        fees,
        payoutAmount,
        vatBreakdown,
        payoutTarget,
    };
};
export { settlementNoteFactory };
